import { BaseCatalogItemTransformer } from './baseCatalogItem.transformer';
import type { Collaboration } from '@/types';
import type { CatalogType } from '../services/catalog/catalog-types';
import { getMediaData, playbackUrl } from '@/lib/utils/media.utils';
import { processTagArray, getYearTag, combineTags } from '@/lib/utils/tagGenerator';

/**
 * Transformer for converting Collaboration entities to CatalogItem
 */
export class CollaborationTransformer extends BaseCatalogItemTransformer<Collaboration> {
  protected getId(collaboration: Collaboration): string | number {
    return collaboration.id.toString();
  }

  protected getType(_collaboration: Collaboration): CatalogType {
    return 'collaboration';
  }

  protected getSlug(collaboration: Collaboration): string {
    return collaboration.slug;
  }

  protected getHref(collaboration: Collaboration): string {
    return `/catalog/collaborations/${collaboration.slug}`;
  }

  protected getTitle(collaboration: Collaboration): string {
    return collaboration.title;
  }

  protected getDisplayTitle(collaboration: Collaboration): string {
    return collaboration.title;
  }

  protected getFilename(collaboration: Collaboration): string {
    return collaboration.slug;
  }

  protected getThumbnail(collaboration: Collaboration): string {
    if (typeof collaboration.cardImage !== 'number') {
      const cardImageData = getMediaData(collaboration.cardImage);
      return cardImageData.url || '';
    }
    return '';
  }

  protected getImageUrl(collaboration: Collaboration): string {
    // Collaborations only use cardImage, no fallback
    return this.getThumbnail(collaboration);
  }

  protected getWidth(collaboration: Collaboration): number {
    if (typeof collaboration.cardImage !== 'number') {
      const cardImageData = getMediaData(collaboration.cardImage);
      return cardImageData.width || 800;
    }
    return 800;
  }

  protected getHeight(collaboration: Collaboration): number {
    if (typeof collaboration.cardImage !== 'number') {
      const cardImageData = getMediaData(collaboration.cardImage);
      return cardImageData.height || 600;
    }
    return 600;
  }

  protected getDate(collaboration: Collaboration): string {
    return collaboration.date || collaboration.createdAt;
  }

  protected getYear(collaboration: Collaboration): number {
    if (collaboration.date) {
      return new Date(collaboration.date).getFullYear();
    }
    return 0;
  }

  protected getDescription(collaboration: Collaboration): string {
    return collaboration.description;
  }

  protected getLongDescription(collaboration: Collaboration): string {
    return collaboration.description;
  }

  protected getVideoUrl(collaboration: Collaboration): string | undefined {
    return playbackUrl(collaboration.video) || undefined;
  }

  protected getTags(collaboration: Collaboration): string[] {
    const yearTag = getYearTag(collaboration.date);
    return combineTags(
      processTagArray(collaboration.tags),
      yearTag && parseInt(yearTag) > 0 ? yearTag : null
    );
  }
}

// Export singleton instance
export const collaborationTransformer = new CollaborationTransformer();
